/**
 * Schemas for the `regions.*` tRPC procedures.
 *
 * Two surfaces:
 *   - `regions.list` — every region for a given outbreak slug, ordered by
 *     `sort_order asc`. Returns the GeoJSON Point so the map component can
 *     render markers without further parsing.
 *   - `regions.byCountry` — flat list of regions inside a country code, used
 *     by the per-country deep-dive pages.
 */

import { z } from "zod";

export const REGION_SCALES = [
  "country",
  "province",
  "city",
  "cluster",
  "maritime",
] as const;
export const regionScaleSchema = z.enum(REGION_SCALES);
export type RegionScale = z.infer<typeof regionScaleSchema>;

export const REGION_STATUSES = ["active", "monitored", "cleared"] as const;
export const regionStatusSchema = z.enum(REGION_STATUSES);
export type RegionStatus = z.infer<typeof regionStatusSchema>;

/** GeoJSON Point — `[longitude, latitude]` in WGS84. */
export const pointGeoJSONSchema = z.object({
  type: z.literal("Point"),
  coordinates: z.tuple([z.number(), z.number()]),
});
export type PointGeoJSON = z.infer<typeof pointGeoJSONSchema>;

export const regionListItemSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  scale: regionScaleSchema,
  status: regionStatusSchema,
  dataText: z.string().nullable(),
  coordinates: pointGeoJSONSchema.nullable(),
  sortOrder: z.number().int(),
  /** ISO 3166-1 alpha-2 country code; null for international waters / unknown. */
  countryCode: z.string().length(2).nullable(),
  /** Latest confirmed cases for this country, joined from country_status. */
  casesConfirmed: z.number().int().nullable(),
  /** Latest deaths for this country, joined from country_status. */
  deaths: z.number().int().nullable(),
  /** Computed deaths / casesConfirmed * 100, rounded to 1 dp; null when undefined. */
  caseFatalityPct: z.number().nullable(),
  /**
   * Total country_status rows for this country in the outbreak — a "signal
   * strength" derived from news-headline country detection. Always 0 or
   * greater; used as a fallback display when `casesConfirmed` is null.
   */
  signalMentions: z.number().int().nonnegative(),
  /** Source URL for data provenance — joined from country_status when available. */
  sourceUrl: z.string().url().nullable(),
  /** Verification timestamp — joined from country_status when available. */
  verifiedAt: z.date().nullable(),
});
export type RegionListItem = z.infer<typeof regionListItemSchema>;

export const regionsListInputSchema = z.object({
  slug: z.string().min(1).max(100),
});

export const regionsByCountryInputSchema = z.object({
  /** ISO 3166-1 alpha-2 country code. */
  country: z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/),
});

/** Compact `{ lat, lng }` form used inside `outbreak.getCurrent`. */
export const regionMarkerSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  scale: regionScaleSchema,
  status: regionStatusSchema,
  dataText: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
});
export type RegionMarker = z.infer<typeof regionMarkerSchema>;
